import { getInvoiceDetailsCardInfo } from "../invoice-master/invoice-helper";
import { RootState } from "src/context/store";
import { useSelector, useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { Spinner } from "src/components";
import { invoiceActions } from "src/context/invoice-slice";
import { fetchPriorityInvoicesData } from "src/services/invoice-api";
import { useToast } from "src/hooks/useToast";
import { useCallback, useMemo } from "react";
import InvoiceApprovalDetailCard from "../invoice-homepage/InvoiceApprovalDetailCard";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import usePermission from "src/hooks/usePermission";

interface InvoiceApprovalPriorityTabProps {
  searchCategory: string;
  searchValue: string;
}

const InvoiceApprovalPriorityTab = ({ searchCategory, searchValue }: InvoiceApprovalPriorityTabProps) => {
  const { invoiceApprovalPriorityList, InvoiceApprovalFilters, invoiceAnalysisData } = useSelector((state: RootState) => state.invoice);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(2);
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const { toast } = useToast();

  const { hasPermission: hasViewDetailsPermission, loading: detailsPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE["VIEW DETAILS"].module,
    action: fineGrainedPermissionsKeys.INVOICE["VIEW DETAILS"].action,
  });

  const { hasPermission: hasPermissionReassign, loading: reassignPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.REASSIGN.module,
    action: fineGrainedPermissionsKeys.INVOICE.REASSIGN.action,
  });
  const { hasPermission: hasPermissionPutOnHold, loading: putOnHoldPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE["PUT ON HOLD"].module,
    action: fineGrainedPermissionsKeys.INVOICE["PUT ON HOLD"].action,
  });
  const { hasPermission: hasPermissionReject, loading: rejectPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.REJECT.module,
    action: fineGrainedPermissionsKeys.INVOICE.REJECT.action,
  });
  const { hasPermission: hasPermissionEscalate, loading: escalatePermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE.ESCALATE.module,
    action: fineGrainedPermissionsKeys.INVOICE.ESCALATE.action,
  });
  const { hasPermission: hasPermissionSetPriority, loading: setPriorityPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.INVOICE["SETPRIORITY/REMOVEPRIORITY"].module,
    action: fineGrainedPermissionsKeys.INVOICE["SETPRIORITY/REMOVEPRIORITY"].action,
  });

  const searchParams = useMemo(
    () => ({
      company_code: InvoiceApprovalFilters?.company?.map((item) => item.code)?.join(",") || "",
      division_code: InvoiceApprovalFilters?.division?.map((item) => item.company_divisioncode)?.join(",") || "",
      startDate: InvoiceApprovalFilters?.dateRange?.from,
      endDate: InvoiceApprovalFilters?.dateRange?.to,
      assignee_id: InvoiceApprovalFilters?.assignee?.map((item) => item.invoice_assignment_id)?.join(",") || "",
      city: InvoiceApprovalFilters?.city?.map((item) => item.city_name?.split("|")?.[0]?.trim())?.join(",") || "",
      district: InvoiceApprovalFilters?.district?.map((item) => item.district_name)?.join(",") || "",
      state: InvoiceApprovalFilters?.state?.map((item) => item.state_name)?.join(",") || "",
      ordering: InvoiceApprovalFilters?.sorting?.[0]?.key,
      fuid_status_id:
        searchValue && searchValue?.length > 0 ? "" : InvoiceApprovalFilters?.status?.map((item) => item.fuid_status_id)?.join(",") || "6,8",
      page_size: 10,
      page_number: 1,
      screen: "Approval",
      ...(searchValue && searchCategory ? { [searchCategory]: searchValue } : {}),
    }),
    [InvoiceApprovalFilters, searchCategory, searchValue]
  );

  const fetchPriorityData = useCallback(
    async () => {
      setIsLoading(true);
      dispatch(invoiceActions.setInvoiceApprovalPriorityList([]));
      try {
        const allInvoicesRes = await fetchPriorityInvoicesData(searchParams);
        dispatch(invoiceActions.setInvoiceApprovalPriorityList(allInvoicesRes?.data?.data?.invoiceData || []));
        if (allInvoicesRes?.data?.data) {
          dispatch(invoiceActions.setInvoiceAnalysisData(
            {
              entry: {
                "All Invoices": invoiceAnalysisData?.entry?.["All Invoices"] || 0,
                "Priority Tasks": invoiceAnalysisData?.entry?.["Priority Tasks"] || 0,
                "Escalated": invoiceAnalysisData?.entry?.["Escalated"] || 0,
              },
              approval: {
                "All Invoices": invoiceAnalysisData?.approval?.["All Invoices"] || 0,
                "Priority Tasks": allInvoicesRes?.data?.data?.total || 0,
                "Escalated": invoiceAnalysisData?.approval?.["Escalated"] || 0,
              }
            }
          ));
        }
        setPage(2);
        setHasMore(true);
        toast({ description: "Invoice data fetched successfully", variant: "default" });
      } catch (error) {
        console.error("Error fetching invoices data:", error);
        toast({ description: "Failed to fetch invoice data", variant: "destructive" });
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, searchParams]
  );

  const fetchMoreData = async () => {
    if (isLoading) return;
    try {
      const nextPageParams = { ...searchParams, page_number: page };
      const response = await fetchPriorityInvoicesData(nextPageParams);
      const newData = response?.data?.data?.invoiceData;
      if (!newData || newData.length === 0) {
        setHasMore(false);
        return;
      }
      dispatch(invoiceActions.setInvoiceApprovalPriorityList([...(invoiceApprovalPriorityList || []), ...newData]));
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching more invoices:", error);
      setHasMore(false);
    }
  };

  useEffect(() => {
    fetchPriorityData();
  }, [fetchPriorityData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        {" "}
        <Spinner />
      </div>
    );
  }

  return (
    <div className="mt-10 w-full">
      {invoiceApprovalPriorityList?.length === 0 && (
        <div className="flex justify-center items-center h-[20vh]">
          <p className="text-lg text-gray-500">No invoices found</p>
        </div>
      )}

      <div id="scrollableDiv" className="h-screen overflow-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
        <InfiniteScroll
          dataLength={invoiceApprovalPriorityList?.length}
          next={fetchMoreData}
          hasMore={hasMore}
          loader={""}
          scrollableTarget="scrollableDiv"
          endMessage={<div className="py-4 text-center text-gray-500">No more invoices to load</div>}
        >
          <ul>
            {invoiceApprovalPriorityList?.map((data: any, index: number) => (
              <li className="my-5 rounded-xl border shadow-sm border-gray-200" key={index}>
                <InvoiceApprovalDetailCard
                  status={data?.fileStatusHdr?.fuid_status_desc}
                  data={getInvoiceDetailsCardInfo(data)}
                  checkDataType={false}
                  invoiceData={data}
                  viewDetails={true}
                  index={index}
                  approvalScreen={true}
                  hasViewDetailsPermission={hasViewDetailsPermission}
                  detailsPermissionLoading={detailsPermissionLoading}
                  hasPermissionReassign={hasPermissionReassign}
                  reassignPermissionLoading={reassignPermissionLoading}
                  hasPermissionPutOnHold={hasPermissionPutOnHold}
                  putOnHoldPermissionLoading={putOnHoldPermissionLoading}
                  hasPermissionReject={hasPermissionReject}
                  rejectPermissionLoading={rejectPermissionLoading}
                  hasPermissionEscalate={hasPermissionEscalate}
                  escalatePermissionLoading={escalatePermissionLoading}
                  hasPermissionSetPriority={hasPermissionSetPriority}
                  setPriorityPermissionLoading={setPriorityPermissionLoading}
                />
              </li>
            ))}
          </ul>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default InvoiceApprovalPriorityTab;
